import axios from "axios";
import { API_BASE_URL } from "../config";
import { forceRefreshAccessToken } from "../keycloakRefresh";

const base = `${API_BASE_URL}/intake`;

function uploadTimeoutMs() {
  return parseInt(process.env.REACT_APP_INTAKE_UPLOAD_TIMEOUT_MS || "3600000", 10);
}

function uploadPollTimeoutMs() {
  return parseInt(
    process.env.REACT_APP_INTAKE_UPLOAD_POLL_TIMEOUT_MS ||
      process.env.REACT_APP_INTAKE_UPLOAD_TIMEOUT_MS ||
      "3600000",
    10
  );
}

function uploadAcceptTimeoutMs() {
  return parseInt(process.env.REACT_APP_INTAKE_UPLOAD_ACCEPT_TIMEOUT_MS || "120000", 10);
}

function uploadPollIntervalMs() {
  return parseInt(process.env.REACT_APP_INTAKE_UPLOAD_POLL_MS || "3000", 10);
}

function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new axios.CanceledError("Upload cancelled"));
      return;
    }
    const timer = setTimeout(resolve, ms);
    if (signal) {
      const onAbort = () => {
        clearTimeout(timer);
        reject(new axios.CanceledError("Upload cancelled"));
      };
      signal.addEventListener("abort", onAbort, { once: true });
    }
  });
}

async function tryRecoverCompletedUpload(
  projectId,
  sessionId,
  baselineTurns,
  baselineCapturedCount = 0
) {
  try {
    const progRes = await axios.get(`${base}/${projectId}/progress`, {
      params: { session_id: sessionId },
      timeout: 120000,
      validateStatus: () => true,
    });
    const progress = progRes.data?.data || progRes.data || {};
    const turns = progress.turns ?? 0;
    const capturedCount = Object.keys(progress.captured || {}).length;
    const hasNewProgress = turns > baselineTurns || capturedCount > baselineCapturedCount;

    const statusRes = await axios.get(`${base}/${projectId}/upload-status`, {
      params: { session_id: sessionId },
      timeout: 120000,
      validateStatus: () => true,
    });
    if (statusRes.data?.status === "complete") {
      return statusRes.data;
    }

    let extractionJson;
    try {
      const ext = await axios.get(`${base}/${projectId}/extraction`, {
        params: { session_id: sessionId },
        timeout: 120000,
      });
      extractionJson = ext.data?.extraction_json;
    } catch {
      /* optional */
    }

    if (!hasNewProgress && !extractionJson) return null;

    return {
      success: true,
      status: "complete",
      session_id: sessionId,
      progress,
      captured: progress.captured,
      scores: progress.scores,
      turns: progress.turns,
      overall: progress.overall_score,
      is_complete: progress.is_complete,
      captured_count: Object.keys(progress.captured || {}).length,
      extraction_json: extractionJson,
      extraction_conflicts: progress.extraction_conflicts || [],
    };
  } catch {
    return null;
  }
}

async function pollUploadStatus(projectId, sessionId, options = {}) {
  const deadline = Date.now() + uploadPollTimeoutMs();
  const baselineTurns = options.baselineTurns ?? 0;
  const baselineCapturedCount = options.baselineCapturedCount ?? 0;
  let polls = 0;

  while (Date.now() < deadline) {
    if (polls > 0) {
      await sleep(uploadPollIntervalMs(), options.signal);
    }
    polls += 1;

    const statusRes = await axios.get(`${base}/${projectId}/upload-status`, {
      params: { session_id: sessionId },
      timeout: 120000,
      signal: options.signal,
      validateStatus: () => true,
    });
    const body = statusRes.data || {};
    if (body.status === "complete") {
      return body;
    }
    if (body.status === "cancelled") {
      // Do not recover from an extraction file on disk — that would map a cancelled document.
      return body;
    }
    if (body.status === "error" || (statusRes.status >= 400 && body.status !== "processing")) {
      const err = new Error(body.error || body.message || "Upload failed");
      err.response = { status: statusRes.status, data: body };
      throw err;
    }
    if (body.status === "processing") {
      // Do not call /progress while extracting — Python agent is busy and QA nginx may 504.
      continue;
    }
    if (body.status === "idle") {
      const recovered = await tryRecoverCompletedUpload(
        projectId,
        sessionId,
        baselineTurns,
        baselineCapturedCount
      );
      if (recovered) return recovered;
      continue;
    }
  }

  const recovered = await tryRecoverCompletedUpload(
    projectId,
    sessionId,
    baselineTurns,
    baselineCapturedCount
  );
  if (recovered) return recovered;

  const err = new Error("Extraction timed out");
  err.code = "ECONNABORTED";
  throw err;
}

export async function fetchIntakeUploadStatus(projectId, sessionId) {
  const res = await axios.get(`${base}/${projectId}/upload-status`, {
    params: { session_id: sessionId },
    timeout: 120000,
    validateStatus: () => true,
  });
  return res.data || {};
}

export { pollUploadStatus };

export async function fetchIntakeApps() {
  const res = await axios.get(`${base}/apps`);
  return res.data;
}

export async function createIntakeSession(projectId, gate0Data = null) {
  const res = await axios.post(`${base}/${projectId}/session`, { gate0_data: gate0Data });
  return res.data;
}

export async function getIntakeSession(projectId) {
  const res = await axios.get(`${base}/${projectId}/session`);
  return res.data;
}

export async function updateIntakeCapturedItem(projectId, sessionId, itemId, value) {
  const res = await axios.patch(`${base}/${projectId}/captured/${encodeURIComponent(itemId)}`, {
    session_id: sessionId,
    value,
  });
  return res.data;
}

export async function resolveIntakeExtractionConflict(projectId, sessionId, conflict, choice) {
  const res = await axios.post(`${base}/${projectId}/resolve-extraction-conflict`, {
    session_id: sessionId,
    item_id: conflict.item_id,
    choice,
    new_value: conflict.new_value,
    source_document: conflict.new_source_document,
    document_index: conflict.document_index,
    extraction_meta: conflict.extraction_meta,
  });
  return res.data;
}

export async function sendIntakeChat(projectId, sessionId, message, draftMessages = null, options = {}) {
  const body = {
    session_id: sessionId,
    message,
  };
  if (Array.isArray(draftMessages) && draftMessages.length) {
    body.draft_messages = draftMessages;
  }
  if (Number.isInteger(options.rewindFromTurn) && options.rewindFromTurn >= 1) {
    body.rewind_from_turn = options.rewindFromTurn;
  }
  if (Array.isArray(options.textHistory) && options.textHistory.length) {
    body.text_history = options.textHistory;
  }
  const res = await axios.post(`${base}/${projectId}/chat`, body);
  return res.data;
}

export async function saveIntakeDraftMessages(projectId, messages) {
  const res = await axios.post(`${base}/${projectId}/chat-messages`, { messages });
  return res.data;
}

export async function pasteIntakeDocument(projectId, sessionId, content, options = {}) {
  const acceptRes = await axios.post(`${base}/${projectId}/documents`, {
    session_id: sessionId,
    content,
  }, {
    timeout: uploadAcceptTimeoutMs(),
    signal: options.signal,
    intakeLongRunning: true,
    validateStatus: (status) => (status >= 200 && status < 300) || status === 202,
  });
  if (acceptRes.status === 202 || acceptRes.data?.processing) {
    return pollUploadStatus(projectId, sessionId, options);
  }
  return acceptRes.data;
}

export async function uploadIntakeFile(projectId, sessionId, file, options = {}) {
  let baselineTurns = 0;
  let baselineCapturedCount = 0;
  try {
    const progRes = await axios.get(`${base}/${projectId}/progress`, {
      params: { session_id: sessionId },
      timeout: 60000,
    });
    const progress = progRes.data?.data || progRes.data || {};
    baselineTurns = progress.turns ?? 0;
    baselineCapturedCount = Object.keys(progress.captured || {}).length;
  } catch {
    /* ignore */
  }

  // Large files can take minutes to transfer; start with a full-lifespan token
  // so it does not expire mid-upload before the backend validates it.
  await forceRefreshAccessToken();

  const form = new FormData();
  form.append("file", file);
  form.append("session_id", sessionId);

  const acceptRes = await axios.post(`${base}/${projectId}/upload`, form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: uploadAcceptTimeoutMs(),
    maxBodyLength: Infinity,
    maxContentLength: Infinity,
    signal: options.signal,
    intakeLongRunning: true,
    validateStatus: (status) => (status >= 200 && status < 300) || status === 202,
  });

  let result = acceptRes.data;
  if (acceptRes.status === 202 || result?.processing) {
    result = await pollUploadStatus(projectId, sessionId, {
      ...options,
      baselineTurns,
      baselineCapturedCount,
    });
  }

  if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
    window.__lastIntakeUploadResponse = result;
    const n = result?.captured_count ?? Object.keys(result?.captured || {}).length;
    console.info(
      `[intake] upload complete — captured=${n}. ` +
        "Or run: copy(JSON.stringify(window.__lastIntakeUploadResponse, null, 2))"
    );
  }
  return result;
}

export async function fetchIntakeExtraction(projectId, sessionId, options = {}) {
  const res = await axios.get(`${base}/${projectId}/extraction`, {
    params: { session_id: sessionId },
    timeout: uploadTimeoutMs(),
    signal: options.signal,
  });
  if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
    window.__lastIntakeExtractionResponse = res.data;
    console.info(
      `[intake] extraction HTTP ${res.status}. ` +
        "DevTools: Network → Fetch/XHR → extraction?session_id=… → Response. " +
        "Or: copy(JSON.stringify(window.__lastIntakeExtractionResponse, null, 2))"
    );
  }
  return res.data;
}

export async function fetchIntakeProgress(projectId, sessionId) {
  const res = await axios.get(`${base}/${projectId}/progress`, {
    params: { session_id: sessionId },
  });
  return res.data;
}

export async function fetchIntakeOutputs(projectId, sessionId) {
  const res = await axios.get(`${base}/${projectId}/outputs`, {
    params: { session_id: sessionId },
  });
  return res.data;
}

export async function submitIntake(projectId, sessionId, scores = {}) {
  const res = await axios.post(`${base}/${projectId}/submit`, {
    session_id: sessionId,
    ...scores,
  });
  return res.data;
}

/** Cancel in-flight async upload — releases lock; late results discarded. */
export async function cancelIntakeUpload(projectId, sessionId) {
  const res = await axios.post(`${base}/${projectId}/upload-cancel`, {
    session_id: sessionId,
  }, {
    timeout: 30000,
  });
  return res.data;
}

/**
 * Successful Gate 1 entry — clears session RAG pickle + extraction cache.
 * Summary complete / Gate 1 entry clears session Document Intelligence caches.
 * View Summary (read-only) must NOT call this — only successful Gate 1 entry /
 * submitIntake / session delete should wipe pickles + extraction cache.
 */
export async function enterGate1(projectId, sessionId) {
  const res = await axios.post(`${base}/${projectId}/enter-gate1`, {
    session_id: sessionId,
  }, {
    timeout: 60000,
  });
  return res.data;
}

